import { create } from 'zustand';
import { BRAND_DNA_JOURNEY_ID } from '@poppy/coach';
import type { PromptAuthor } from '@/app/(protected)/boards/[boardId]/PromptsPanel/types';
import { isGuidedTourActive } from '@/lib/guidedTourState';

type PanelType =
  | 'notes'
  | 'discovery'
  | 'search-creators'
  | 'version-control'
  | 'prompts'
  | 'collections'
  | 'group-items'
  | 'facebook-ads'
  | 'copilot'
  | 'creations';

/** Controls which view the Prompts panel opens on. Defaults to browsing all prompts. */
export type PromptPanelState = {
  mode: 'list' | 'view' | 'edit' | 'create';
  promptId?: string;
  promptKind?: 'standard' | 'reference';
  searchQuery?: string;
  listTab?: 'all' | 'yours' | 'team';
  seedText?: string;
  authorFilter?: PromptAuthor;
  /** Stamped by openPanel so re-opening with identical fields still remounts. */
  requestId?: number;
};

type PanelMetadata = {
  /** The current guided Copilot search, shared with the tutorial worker. */
  guidedDiscover?: {
    journeyId: string;
    boardId: string;
    startedAt: number;
    search: import('@/lib/discover/copilotSearch').CopilotDiscoverSearch;
  };
  /** A completed Copilot library search, opened without running or charging it again. */
  discoverSearch?: import('@/lib/discover/copilotSearch').CopilotDiscoverSearch;
  /** Guided research keeps selected examples in its existing Inspiration group. */
  discoverAddToBoard?: ((urls: string[]) => Promise<boolean>) | null;
  profileUrl?: string;
  /** When set, SearchCreatorsPanel focuses the search input, then clears this flag. */
  focusSearch?: boolean;
  /** When set, SearchCreatorsPanel selects this platform (as a chip click would), then clears it. */
  selectPlatform?: 'youtube' | 'instagram' | 'tiktok' | 'linkedin';
  /** When set, the Discovery Panel is in "onboarding mode": selecting posts calls this
   *  instead of placing nodes at canvas center.  */
  onboardingPostCallback?: ((urls: string[]) => void) | null;
  /** Deep-link state for the Prompts panel. Absent = browse all (list mode). */
  promptPanel?: PromptPanelState;
  /** Deep-link the Collections panel to a vault (and optionally expand a group).
   *  Used by reference chips' "View content" / double-click. */
  collections?: { vaultId: string; groupId?: string; requestId?: number };
  /** Deep-link the board group items panel to a regular group node. */
  boardGroup?: { groupNodeId: string; requestId?: number };
  /**
   * A message for the Copilot from a coach journey: typed in for the person, or sent. The
   * `instructions` travel with a sent message but never show: how the Copilot runs this part.
   */
  copilot?: {
    prompt: string;
    send: boolean;
    instructions?: string;
    label?: string;
    context?: import('@poppy/coach').CoachCopilotContext;
    requestId?: number;
    owner?: { journeyId: string; boardId: string; stepId: string; startedAt?: number };
  };
};

type SidePanelStoreState = {
  isOpen: boolean;
  activePanelType: PanelType | null;
  /** Metadata for the currently active panel (convenience alias). */
  metadata: PanelMetadata;
  /** Per-panel metadata so hidden panels keep their props when switching. */
  panelMetadataByType: Partial<Record<PanelType, PanelMetadata>>;
  /** Panel types that have been opened at least once and stay mounted. */
  mountedPanelTypes: PanelType[];
  openPanel: (type: PanelType, metadata?: PanelMetadata) => void;
  closePanel: () => void;
  /**
   * A clean panel for a board that just loaded. The Brand DNA onboarding's Copilot message for
   * this very board (`boardId`) survives it: the onboarding opened it as the board arrived.
   */
  resetForBoardLoad: (boardId?: string) => void;
};

const RIGHT_TOOLBAR_PANEL_TYPES: ReadonlySet<PanelType> = new Set([
  'search-creators',
  'prompts',
  'notes',
  'collections',
  'group-items',
  'facebook-ads',
  'copilot',
  'creations',
]);

export function isRightToolbarPanelType(type: PanelType | null): type is PanelType {
  return type != null && RIGHT_TOOLBAR_PANEL_TYPES.has(type);
}

export function isRightToolbarPanelOpen(): boolean {
  const { isOpen, activePanelType } = useSidePanelStore.getState();
  return isOpen && activePanelType != null && RIGHT_TOOLBAR_PANEL_TYPES.has(activePanelType);
}

export function closeRightToolbarPanelIfOpen(): boolean {
  if (!isRightToolbarPanelOpen()) return false;
  useSidePanelStore.getState().closePanel();

  const active = document.activeElement;
  if (active instanceof HTMLElement && active.closest('#board-right-toolbar')) {
    active.blur();
  }

  return true;
}

const createSidePanelStore = () =>
  create<SidePanelStoreState>()(set => ({
    isOpen: false,
    activePanelType: null,
    metadata: {},
    panelMetadataByType: {},
    mountedPanelTypes: [],
    openPanel: (type: PanelType, metadata?: PanelMetadata) =>
      set(state => {
        // Mid-vault-tour, switching panels (prompts, notes, …) — or re-opening
        // collections with a deep-link that rescopes the panel — would unmount
        // the tour's anchors and abort it. Any openPanel while the tour holds
        // the collections panel is either redundant or disruptive: no-op.
        if (state.isOpen && state.activePanelType === 'collections' && isGuidedTourActive()) {
          return state;
        }
        const existingForType = state.panelMetadataByType[type] ?? {};
        let nextForType =
          metadata !== undefined ? { ...existingForType, ...metadata } : existingForType;
        // An explicit prompts deep-link must navigate even when its fields are
        // value-identical to a previous open: the panel stays mounted, so the
        // remount key only changes if something in the metadata does.
        if (metadata?.promptPanel) {
          nextForType = {
            ...nextForType,
            promptPanel: {
              ...metadata.promptPanel,
              requestId: (existingForType.promptPanel?.requestId ?? 0) + 1,
            },
          };
        }
        // Same for Collections deep-links: the panel stays mounted, so scoping
        // it to a vault/group on a repeat open needs a changing remount key.
        if (metadata?.collections) {
          nextForType = {
            ...nextForType,
            collections: {
              ...metadata.collections,
              requestId: (existingForType.collections?.requestId ?? 0) + 1,
            },
          };
        }
        if (metadata?.copilot) {
          nextForType = {
            ...nextForType,
            copilot: {
              ...metadata.copilot,
              requestId: (existingForType.copilot?.requestId ?? 0) + 1,
            },
          };
        }
        if (metadata?.boardGroup) {
          nextForType = {
            ...nextForType,
            boardGroup: {
              ...metadata.boardGroup,
              requestId: (existingForType.boardGroup?.requestId ?? 0) + 1,
            },
          };
        }

        return {
          isOpen: true,
          activePanelType: type,
          metadata: nextForType,
          panelMetadataByType: { ...state.panelMetadataByType, [type]: nextForType },
          mountedPanelTypes: state.mountedPanelTypes.includes(type)
            ? state.mountedPanelTypes
            : [...state.mountedPanelTypes, type],
        };
      }),
    // While the guided vault tour runs, closing the vault panel (X, V, the
    // toolbar toggle — every path funnels here) would unmount every tour
    // anchor and abort the tour mid-step. Suppress it; Escape ends the tour
    // itself (joyride's dismiss key), after which closing works again.
    closePanel: () =>
      set(state =>
        state.isOpen && state.activePanelType === 'collections' && isGuidedTourActive()
          ? state
          : { isOpen: false }
      ),
    resetForBoardLoad: boardId =>
      set(state => {
        const copilot = state.panelMetadataByType.copilot?.copilot;
        // Only the Brand DNA onboarding opens its Copilot as the board arrives.
        if (
          boardId &&
          copilot?.owner?.boardId === boardId &&
          copilot.owner.journeyId === BRAND_DNA_JOURNEY_ID
        ) {
          const kept = { copilot };
          return {
            isOpen: true,
            activePanelType: 'copilot',
            metadata: kept,
            panelMetadataByType: { copilot: kept },
            mountedPanelTypes: ['copilot'],
          };
        }
        return {
          isOpen: false,
          activePanelType: null,
          metadata: {},
          panelMetadataByType: {},
          mountedPanelTypes: [],
        };
      }),
  }));

// The production bundle emits this store's `create(...)` body into more than one
// chunk (confirmed: `activePanelType:null` appears in two separate emitted
// chunks). That gives the paste handlers (`handleInstagramProfilePaste`, etc.) a
// different store instance than the rendered `SidePanel`, so `openPanel(...)`
// from a paste updates a store nothing is subscribed to — the panel never opens,
// with no error — while click-driven opens (version history, search creators)
// work because they share the panel's chunk. Caching the instance on `globalThis`
// forces every duplicated copy to resolve to one shared store.
const globalForSidePanelStore = globalThis as unknown as {
  __sidePanelStore?: ReturnType<typeof createSidePanelStore>;
};

export const useSidePanelStore =
  globalForSidePanelStore.__sidePanelStore ??
  (globalForSidePanelStore.__sidePanelStore = createSidePanelStore());
